import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Highlight,
  Snippet,
  useInfiniteHits,
  useInstantSearch,
  useRefinementList,
  useSearchBox,
} from 'react-instantsearch'

import Link from '@/components/link-component'
import ChatBoxIcon from '@/icons/bwi/chat-box.svg'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import { useLocalStorage } from 'usehooks-ts'
import { router } from '@inertiajs/react'
import IconFont from '@/components/ui/icon-font'
import DblAngleRightIcon from '@/icons/bwi/dbl-angle-right.svg'
import HitPreview from './hit-preview'
import { ICONS } from './icons'

import { cx } from '@/utils/strings'
import { HitProps } from './types'

type InfiniteHitsProps = {
  className?: string
  variant?: 'list' | 'preview'
  /** Group results by type. Disables load more if enabled */
  shouldGroupResults?: boolean
  showIcons?: boolean
  onLinkClick?: (hit: HitProps, event: React.MouseEvent<HTMLAnchorElement>) => void
  onLinkPreview?: (hit: HitProps | null) => void
}

export default function InfiniteHits({
  className,
  variant = 'list',
  shouldGroupResults = false,
  showIcons = true,
  onLinkClick,
  onLinkPreview: onLinkSelect
}: InfiniteHitsProps) {
  const { query } = useSearchBox()
  const { items, showMore, isLastPage, sendEvent, results } = useInfiniteHits<HitProps>()
  const { items: facetItems, refine } = useRefinementList({ attribute: 'type' })
  const type = useMemo(() => facetItems?.find(({ isRefined }) => isRefined)?.value, [facetItems])
  

  const [activeHit, setActiveHit] = useState<HitProps | null>(null)
  useEffect(() => {
    onLinkSelect?.(activeHit)
  }, [activeHit])

  const getHitElement = (hit: HitProps) =>
    document.querySelector<HTMLAnchorElement>(`[data-object-id="${activeHit?.objectID}"]`)

  // Set the first hit as active when the results change
  useEffect(() => {
    if (variant === 'preview') {
      setActiveHit(items?.[0] ?? null)
    }
  }, [results?.queryID])

  // Scroll the active hit into view
  useEffect(() => {
    if (activeHit && usedKeyboard) {
      const el = getHitElement(activeHit)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const onFocus = (e: FocusEvent) => {
        e.preventDefault()
      }
      el?.addEventListener('focus', onFocus)
      el?.focus()

      return () => {
        el?.removeEventListener('focus', onFocus)
      }
    }
  }, [activeHit])

  // Keeping track if the user used the keyboard to prevent conflicts
  const [usedKeyboard, setUsedKeyboard] = useState(false)
  // Add keyboard shortcuts
  useEffect(() => {
    const moveActiveHit = (direction: 'up' | 'down') => {
      const index = items.findIndex((hit) => hit.objectID === activeHit?.objectID)
      const nextItem = items[direction === 'down' ? index + 1 : index - 1]
      if (nextItem) {
        setActiveHit(nextItem)
      }
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'ArrowDown') {
        moveActiveHit('down')
        setUsedKeyboard(true)
      }
      if (event.key === 'ArrowUp') {
        moveActiveHit('up')
        setUsedKeyboard(true)
      }
      if (event.key === 'Enter' && activeHit) {
        const el = getHitElement(activeHit)
        el?.click()
      }
    }
    const onMouseMove = () => {
      setUsedKeyboard(false)
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('mousemove', onMouseMove)
    return (): void => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('mousemove', onMouseMove)
    }
  }, [activeHit, items])

  const smartSearchSuggestion = useMemo(() => {
    return (
      <button
        className="text-left flex items-center rounded-xl hover:bg-secondary-50 hover:dark:bg-secondary-600/80 p-4 cursor-pointer select-none border-secondary-100 border-[1px] mb-4 transition-colors"
        type="button"
        title={`Search for ${query} in Smart Search`}
        onClick={(e) => {
          e.preventDefault()
          setSmartSearchQuery(query)
          router.visit('/smart-search/')
          return false
        }}
        onAuxClick={(e) => {
          e.preventDefault()
          setSmartSearchQuery(query)
          window.open('/smart-search/', '_blank', 'noreferrer,noopener')
          return false
        }}
      >
        <ChatBoxIcon className="w-5 h-5 mr-3 text-primaryBlue" />
        <div className="flex-1">
          <h4 className="mb-0 text-base text-indigoBlue dark:text-white">
            Discover more about <span className="text-primaryBlue">{query}</span> with Smart Search
          </h4>
          <p className="text-sm text-secondary-500 dark:text-secondary-300 mb-0">
            Explore this conversational AI search for detailed responses about Bitwarden
          </p>
        </div>
      </button>
    )
  }, [query])

  const [smartSearchQuery, setSmartSearchQuery] = useLocalStorage<string | undefined>('smartSearchQuery', undefined)

  return (
    query.length > 0 && (
      <div className={className}>
        {!(items.length > 0) ? (
          <div className="mt-5 px-5">
            <div className="mb-5">{smartSearchSuggestion}</div>
            0 results for &ldquo;<strong>{query}</strong>&rdquo; {type && `in ${type}.`}
            {type && facetItems?.reduce((sum, item) => sum + item.count, 0) > 0 && (
              <p>
                See{' '}
                <button
                  className="text-primaryBlue"
                  type="button"
                  onClick={() => {
                    facetItems?.forEach((item) => {
                      if (item.isRefined) refine(item.value)
                    })
                    refine('')
                  }}
                >
                  all results
                </button>
              </p>
            )}
          </div>
        ) : (
          <div className={cx(variant === 'preview' && 'relative flex h-full')}>
            <div
              className={cx(
                variant === 'list' && 'flex flex-col gap-2',
                variant === 'preview' &&
                  'flex flex-col gap-2 w-full md:w-1/2 max-h-full overflow-auto p-5 border-r-[1px] border-secondary-50 dark:border-secondary-700 scrollbar-transparent'
              )}
              onKeyDown={(e) => {
                // Prevent scrolling with arrow keys because we scroll the active item manually
                if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault()
                }
              }}
            >
              {smartSearchSuggestion}
              {items.map((hit, index) => {
                if (!hit || !hit.url) return null
                const isHighlighted = hit.objectID === activeHit?.objectID
                const group = hit.category
                const prevGroup = index > 0 ? items[index - 1].category : null
                const prevGroupIsDifferent = index > 0 ? prevGroup !== group : false
                return (
                  <Fragment key={hit.objectID}>
                    {shouldGroupResults && (index === 0 || prevGroupIsDifferent) && (
                      <div className={cx('text-primaryBlue font-semibold uppercase mb-2', index > 0 && 'mt-4')}>
                        {group}
                      </div>
                    )}
                    <Link
                      key={hit.objectID}
                      data-object-id={hit.objectID}
                      to={`${hit.url}${hit.hash ? `#${hit.hash}` : ''}`}
                      className={cx(
                        'rounded-sm px-4 pt-2 pb-3 block',
                        variant === 'list' && 'hover:bg-secondary-50 dark:hover:bg-secondary-700/80 hover:shadow-sm',
                        isHighlighted && variant === 'preview' && 'bg-secondary-50 dark:bg-secondary-700'
                      )}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        sendEvent('click', hit, 'Result Clicked')
                        sendEvent('conversion', hit, 'Result Converted')
                        onLinkClick?.(hit, e)
                      }}
                      onFocus={(e: FocusEvent) => {
                        e.preventDefault()
                        setUsedKeyboard(true)
                        setActiveHit(hit)
                      }}
                    >

                      <div
                        className="overflow-hidden"
                        onMouseEnter={() => {
                          if (variant === 'preview') {
                            if (!usedKeyboard) setActiveHit(hit)
                            const activeElement = document.activeElement as HTMLAnchorElement
                            if (activeElement?.dataset?.objectId) {
                              activeElement?.blur()
                            }
                          }
                        }}
                      >
                        {variant === 'list' && (
                          <div className='text-eyebrow uppercase text-secondary-500 dark:text-secondary-300 mb-1'>
                            <span className="text-sm">
                              {showIcons && hit.type && (
                                <IconFont className="text-xl leading-4 p-1" unicode={ICONS[hit.type] || 'e96e'} />
                              )}
                            </span>
                            {hit.type}
                          </div>
                        )}
                        {variant === 'preview' && (
                          <div className='mb-1'>
                            {hit.breadcrumbs?.slice(1).map((breadcrumb, index) => (
                              <div key={index} className="inline-flex text-sm text-secondary-500 dark:text-secondary-200 items-center justify-center whitespace-pre">
                                {index > 0 && (<DblAngleRightIcon className="w-2 h-2 mx-1" />)}
                                {breadcrumb}
                              </div>
                            ))}
                          </div>
                        )}
                        <h4 className={cx('mb-0 text-lg text-secondary-900 dark:text-white font-[500]')}>
                          <Highlight
                            hit={hit}
                            attribute="title"
                            classNames={{
                              highlighted: cx('dark:text-secondary-900'),
                            }}
                          />
                        </h4>
                        {variant === 'list' && (
                          <p className={cx('text-sm mb-0 text-secondary-500 dark:text-secondary-300')}>
                            <Snippet
                              hit={hit}
                              attribute="body"
                              classNames={{
                                highlighted: cx('dark:text-secondary-900'),
                              }}
                            />
                          </p>
                        )}
                      </div>
                    </Link>
                  </Fragment>
                )
              })}
              {!isLastPage && !shouldGroupResults && (
                <div className="w-full text-center pt-8">
                  <CtaLink asButton className="rounded-full px-6 py-2" style={buttonStyles.btnOutlined} onClick={showMore}>
                    Show more results
                  </CtaLink>
                </div>
              )}
            </div>
            {variant === 'preview' && activeHit && (
              <HitPreview hit={activeHit} onLinkClick={onLinkClick} />
            )}
          </div>
        )}
      </div>
    )
  )
}

function highlightType(slug: string) {
  const matches = slug.match(/^\/([a-z]+)(\/.*)$/)
  return matches?.length === 3 ? (
    <> 
      /<mark className="bg-secondary-100 text-xs rounded-sm dark:bg-gold-300 dark:text-seconday-800 px-0.5">{matches[1]}</mark>
      {matches[2]}
    </>
  ) : (
    slug
  )
}
